"use client";
import React, { useRef } from "react";
import Image from "next/image";
import { QuizOption } from "../type";
import { SkeletonLoader } from "./SkeletonLoader";
import { OptionsContainer } from "./OptionsContainer";

export interface CompactQuizVariantProps {
  title: string;
  description?: string;
  options: QuizOption[];
  selectedOptions: string[];
  optionsVariant?: "basic" | "card";
  selectionType?: "single" | "multiple";
  isLoading?: boolean;
  liveMessage?: string;
  onToggle: (value: string, label: string) => void;
  quizRef?: React.Ref<HTMLDivElement>;
  fieldRef?: React.Ref<HTMLDivElement>;
  headingRef?: React.RefObject<HTMLHeadingElement | null>;
}

export const CompactQuizVariant: React.FC<CompactQuizVariantProps> = ({
  title,
  description,
  options,
  selectedOptions,
  optionsVariant = "basic",
  selectionType = "single",
  isLoading = false,
  liveMessage,
  onToggle,
  quizRef,
  fieldRef,
  headingRef,
}) => {
  const internalHeadingRef = useRef<HTMLHeadingElement>(null);
  const effectiveHeadingRef = headingRef || internalHeadingRef;

  return (
    <div
      ref={quizRef}
      className="container font-sans rounded-xl flex flex-col gap-4 md:gap-6 p-4 md:p-8 bg-putty-300 min-h-[220px]"
    >
      {liveMessage && (
        <div
          className="sr-only"
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          {liveMessage}
        </div>
      )}
      <div className="flex flex-col text-start">
        <div ref={fieldRef} className="flex items-center gap-2">
          <Image
            src="/icons/ai-icon.png"
            alt="ai-icon"
            width={36}
            height={36}
            className="m-0 flex-shrink-0"
          />
          <h3
            ref={effectiveHeadingRef}
            className="text-xl font-bold text-gray-900 m-0"
            tabIndex={-1}
          >
            {title}
          </h3>
        </div>
        {description && <p className="m-0 mt-4">{description}</p>}
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        {isLoading ? (
          <SkeletonLoader variant="basic" count={5} sectionTitle={title} />
        ) : (
          <OptionsContainer
            options={options}
            selectedOptions={selectedOptions}
            variant={optionsVariant}
            showAllOptions={false}
            onToggle={onToggle}
            selectionType={selectionType}
            sectionTitle={title}
          />
        )}
      </div>
    </div>
  );
};

export default CompactQuizVariant;
